/**
 * ThemeManager - Manages game themes (dark, light, psychedelic)
 * Persists theme preference to localStorage
 */

export type Theme = 'dark' | 'light' | 'psychedelic';

export class ThemeManager {
  private currentTheme: Theme = 'dark';
  private readonly STORAGE_KEY = 'paperclips-theme';
  private themeButton: HTMLElement | null = null;

  constructor() {
    this.loadTheme();
    this.init();
  }

  private init() {
    // Create theme switcher button
    this.createThemeSwitcher();
  }

  private createThemeSwitcher() {
    let button = document.getElementById('theme-switcher');
    
    if (!button) {
      button = document.createElement('button');
      button.id = 'theme-switcher';
      button.className = 'theme-switcher';
      document.body.appendChild(button);
    }
    
    button.setAttribute('aria-label', 'Switch theme');
    button.innerHTML = this.getThemeIcon();
    
    // Remove existing event listeners by cloning (simple way) or just add new one if we are careful
    // Since we are initializing, we can assume we handle the click
    button.onclick = () => this.cycleTheme();

    this.themeButton = button;
  }

  private getThemeIcon(): string {
    switch (this.currentTheme) {
      case 'dark':
        return '🌙'; // Moon for dark theme
      case 'light':
        return '☀️'; // Sun for light theme
      case 'psychedelic':
        return '✨'; // Sparkles for psychedelic theme
      default:
        return '🌙';
    }
  }

  /**
   * Load theme from localStorage or use default
   */
  private loadTheme() {
    const saved = localStorage.getItem(this.STORAGE_KEY) as Theme | null;
    if (saved && ['dark', 'light', 'psychedelic'].includes(saved)) {
      this.currentTheme = saved;
    }
    this.applyTheme();
  }

  /**
   * Apply theme to document
   */
  private applyTheme() {
    // Remove all theme classes
    document.body.classList.remove('theme-dark', 'theme-light', 'theme-psychedelic');

    // Add current theme class
    if (this.currentTheme !== 'dark') {
      document.body.classList.add(`theme-${this.currentTheme}`);
    }

    // Update meta theme-color
    const themeColors: Record<Theme, string> = {
      dark: '#0a0a0f',
      light: '#f5f5f7',
      psychedelic: '#0a0a1a',
    };

    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute('content', themeColors[this.currentTheme]);
    }

    // Save to localStorage
    localStorage.setItem(this.STORAGE_KEY, this.currentTheme);

    // Update button icon
    if (this.themeButton) {
      this.themeButton.innerHTML = this.getThemeIcon();
    }
  }

  /**
   * Cycle through themes
   */
  public cycleTheme() {
    const themes: Theme[] = ['dark', 'light', 'psychedelic'];
    const currentIndex = themes.indexOf(this.currentTheme);
    const nextIndex = (currentIndex + 1) % themes.length;
    const nextTheme = themes[nextIndex];
    if (nextTheme) {
      this.setTheme(nextTheme);
    }
  }

  /**
   * Set specific theme
   */
  public setTheme(theme: Theme) {
    if (theme !== this.currentTheme) {
      this.currentTheme = theme;
      this.applyTheme();
    }
  }

  /**
   * Get current theme
   */
  public getTheme(): Theme {
    return this.currentTheme;
  }

  /**
   * Check if using psychedelic theme (for special effects)
   */
  public isPsychedelic(): boolean {
    return this.currentTheme === 'psychedelic';
  }

  /**
   * Check if using light theme (for contrast-sensitive effects)
   */
  public isLight(): boolean {
    return this.currentTheme === 'light';
  }
}
